/* Single Tailwind v4 entry: also layers in the Hammer UI preset + brand CSS, theme/theme.css and slick-carousel */
import '@/tailwind.css';
import { initCookiebotBannerDelegation } from '@liveauctioneers/hammer-ui-core/cookieConsent';
import { ErrorBoundary } from '@liveauctioneers/hammer-ui-core/providers';
import { MeltedMac } from '@liveauctioneers/hammer-ui-liveauctioneers/meltedMac';
import { loadableReady } from '@loadable/component';
import * as React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import ReactModal from 'react-modal';
import { BrowserRouter } from 'react-router-dom';
import { PersistorConfig, persistStore } from 'redux-persist';
import Cookies from 'universal-cookie';

import { getBasename } from '@/client/getBasename';
import { readServerData } from '@/client/readServerData';
import { syncTrackingContextWithStore } from '@/client/syncTrackingContextWithStore';
import { initializeAmplitudeAnalyticsAndExperimentation } from '@/components/AmplitudeExperimentsProvider/initializeAmplitudeSDK.client';
import { AppHammerUIProvider } from '@/components/AppHammerUIProvider/AppHammerUIProvider';
import Provider from '@/components/IntlRedux/Provider';
import { PreviousLocationProvider } from '@/components/PreviousLocation/PreviousLocation';
import { setDeviceId, setPerfLoggerHooks } from '@/network/trackingContext.client';
import App from '@/pages/App/App';
import { logClientPerformanceStats } from '@/redux/modules/apiPerformanceStats';
import { getDeployment } from '@/redux/modules/config';
import { getIsWhiteLabel } from '@/redux/modules/whiteLabel';
import { getInitialClientStore } from '@/redux/store';
import AppRoutes, { routes as getRoutes } from '@/routes';
import { isSoft404Page } from '@/ssr/utils/isSoft404Page';
import { getForcedExperimentVariantOverrides } from '@/utils/amplitude/experiments/utils/forcedExperimentOverrides';
import { getFeatureFlagCookies, mergeFeatureFlags } from '@/utils/amplitude/experiments/utils/variantsToFeatureFlags';
import * as apiPerfLogger from '@/utils/apiPerformanceLogger';
import { buildUrlFromLocation } from '@/utils/urls';

import { AmplitudeExperimentsClientProvider } from './AmplitudeExperimentsClientProvider';
import NoCookies from './NoCookies';
import RouteDataLoader from './RouteDataLoader';

const localStoragePersistConfig: PersistorConfig = {
    debounce: 33,
    keyPrefix: 'la:',
    whitelist: ['forgotPassword', 'persist', 'recentItem'],
};

const helmetContext = {};

const localStorageCheck = () => {
    const test = 'test';
    try {
        localStorage.setItem(test, test);
        localStorage.removeItem(test);
        return true;
    } catch {
        return false;
    }
};

// Create the redux store and routing history
readServerData();
const store = getInitialClientStore();

syncTrackingContextWithStore(store);

initCookiebotBannerDelegation();

const amplitudeDeploymentKey = store.getState().config.amplitudeDeploymentKey;

// non-production QA override layer (?forceExperiment=<flagKey>:<variant>) — {} in production
const forcedExperimentVariantOverrides = getForcedExperimentVariantOverrides(getDeployment(store.getState()));

// dev-tools feature flag overrides (Cmd+/ overlay), ignored on prod; read once at boot, the overlay
// asks for a reload. doNotParse keeps "true" a string, as on the server, or hydration breaks.
const featureFlagCookies = getFeatureFlagCookies(
    getDeployment(store.getState()),
    new Cookies().getAll({ doNotParse: true })
);

initializeAmplitudeAnalyticsAndExperimentation(amplitudeDeploymentKey, {
    hasEverPlacedBid: store.getState().user.userData.hasEverPlacedBid,
    hasEverRegisteredForAuction: store.getState().user.userData.hasEverRegisteredForAuction,
}).then(({ amplitudeExperimentsClient }) => {
    /**
     * Wait for all loadable components to be loaded
     * @see https://loadable-components.com/docs/api-loadable-component/#loadableready
     */
    loadableReady(() => {
        /**
         * Render the client-side app
         */

        const dest = document.getElementById('content');
        const basename = getBasename();

        ReactModal.setAppElement('#content');

        const isWhiteLabel = getIsWhiteLabel(store.getState());

        setDeviceId(window.__amplitudeExperiments.deviceId);

        // no route reads experiment flags today, but merge the override layer so a future
        // flag-gated route honors forceExperiment instead of silently ignoring it
        const amplitudeExperimentFlags = {
            ...window.__amplitudeExperiments.variants,
            ...forcedExperimentVariantOverrides,
        };

        // Get application routes
        const routes = getRoutes({
            amplitudeExperimentFlags,
            // the server's variants only: forceExperiment overrides apply after mount, and a
            // flag-gated route must resolve the same way it did in the server render
            featureFlags: mergeFeatureFlags(
                window.__featureFlags,
                window.__amplitudeExperiments.variants,
                featureFlagCookies
            ),
            isWhiteLabel,
        });

        // sanitize and redirect pathname if it contains %0d or %0a (crlf injection)
        if (
            window?.location?.pathname.toLowerCase().includes('%0d') ||
            window?.location?.pathname.toLowerCase().includes('%0a')
        ) {
            const pathname = window.location.pathname.replace(/(.*?)%0[dD]|%0[aA].*/gm, '$1');
            const location = {
                hash: window.location?.hash,
                pathname: pathname,
                search: window.location?.search,
            };
            const redirect = buildUrlFromLocation(location, window.location?.origin);
            window.location.assign(redirect);
            return;
        }

        // record api performance metrics
        const { hooks: perfLoggerHooks, requests: apiMetrics } = apiPerfLogger.createRequestLogger(performance);
        setPerfLoggerHooks(perfLoggerHooks);

        // save to local storage
        persistStore(store, localStoragePersistConfig);

        if (!localStorageCheck() && dest) {
            const root = createRoot(dest!);
            return root.render(<NoCookies />);
        }

        // Soft 404: leave URL the same and return status 404 (item / price-result / guide article).
        // Pass the path (not a sticky boolean) so SPA navigation can recover after a soft-404.
        const bootstrapPath = window?.location?.pathname ?? '';
        const soft404Path = isSoft404Page(bootstrapPath, store.getState()) ? bootstrapPath : undefined;

        // compute performance stats from recorded metrics
        setTimeout(() => {
            const computedApiStats = apiPerfLogger.getStats(apiMetrics);
            store.dispatch(logClientPerformanceStats(computedApiStats, window.location.href));
        }, 5000);

        // Same provider order as the server render in ssr-react-fastify.tsx, so hydration matches.
        // ErrorBoundary stays below AppHammerUIProvider because its MeltedMac fallback is hammer-ui.
        const TheApp = () => (
            <AmplitudeExperimentsClientProvider
                client={amplitudeExperimentsClient}
                deviceId={window.__amplitudeExperiments.deviceId}
                featureFlagCookies={featureFlagCookies}
                forcedVariantOverrides={forcedExperimentVariantOverrides}
                userId={window.__amplitudeExperiments.userId}
                variants={window.__amplitudeExperiments.variants}
            >
                <AppHammerUIProvider baseFeatureFlags={window.__featureFlags}>
                    <ErrorBoundary
                        fallback={() => <MeltedMac />}
                        meta={{ name: 'AppRoot' }}
                        onError={(error, errorInfo) => {
                            console.error('caught error', error, errorInfo);
                        }}
                    >
                        <HelmetProvider context={helmetContext}>
                            <Provider
                                key="provider"
                                store={store}
                            >
                                <BrowserRouter basename={basename}>
                                    <PreviousLocationProvider>
                                        <App>
                                            <RouteDataLoader
                                                routes={routes}
                                                store={store}
                                            >
                                                <AppRoutes
                                                    soft404Path={soft404Path}
                                                    whiteLabel={isWhiteLabel}
                                                />
                                            </RouteDataLoader>
                                        </App>
                                    </PreviousLocationProvider>
                                </BrowserRouter>
                            </Provider>
                        </HelmetProvider>
                    </ErrorBoundary>
                </AppHammerUIProvider>
            </AmplitudeExperimentsClientProvider>
        );

        hydrateRoot(dest, <TheApp />);

        if (process.env.NODE_ENV !== 'production') {
            window.React = React; // enable debugger
        }
    });
});
